import { Position } from "@xyflow/react"
import type { Node } from "@xyflow/react"
import { DEFAULT_NODE_SIZE } from "../constants"

interface EdgeConnectionPoints {
    sourceX: number
    sourceY: number
    sourcePosition: Position
    targetX: number
    targetY: number
    targetPosition: Position
}

function getIconPosition(node: Node, nodeSize: number): { x: number; y: number } {
    const measuredWidth = node.measured?.width ?? nodeSize
    const offsetX = (measuredWidth - nodeSize) / 2
    return {
        x: node.position.x + offsetX,
        y: node.position.y
    }
}

function getPositionFromAngle(dirX: number, dirY: number): Position {
    if (Math.abs(dirX) > Math.abs(dirY)) {
        return dirX > 0 ? Position.Right : Position.Left
    }
    return dirY > 0 ? Position.Bottom : Position.Top
}

/**
 * Calculate edge connection points between two nodes based on circular node geometry.
 * Edges connect from/to the circumference of circular nodes rather than center points.
 *
 * @param sourceNode - The source node
 * @param targetNode - The target node
 * @param nodeSize - The diameter of circular nodes (default: 50)
 */
export function getEdgeConnectionPoints(sourceNode: Node, targetNode: Node, nodeSize = DEFAULT_NODE_SIZE): EdgeConnectionPoints {
    const radius = nodeSize / 2

    const sourceIconPos = getIconPosition(sourceNode, nodeSize)
    const targetIconPos = getIconPosition(targetNode, nodeSize)

    const sourceCenter = {
        x: sourceIconPos.x + radius,
        y: sourceIconPos.y + radius
    }
    const targetCenter = {
        x: targetIconPos.x + radius,
        y: targetIconPos.y + radius
    }

    const dx = targetCenter.x - sourceCenter.x
    const dy = targetCenter.y - sourceCenter.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    if (distance === 0) {
        return {
            sourceX: sourceCenter.x,
            sourceY: sourceCenter.y,
            sourcePosition: Position.Bottom,
            targetX: targetCenter.x,
            targetY: targetCenter.y,
            targetPosition: Position.Top
        }
    }

    const dirX = dx / distance
    const dirY = dy / distance

    const sourceX = sourceCenter.x + dirX * radius
    const sourceY = sourceCenter.y + dirY * radius
    const targetX = targetCenter.x - dirX * radius
    const targetY = targetCenter.y - dirY * radius

    const sourcePosition = getPositionFromAngle(dirX, dirY)
    const targetPosition = getPositionFromAngle(-dirX, -dirY)

    return { sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition }
}
